import { Checkbox, Flex, Text } from "@chakra-ui/react";
import { Modal } from "antd";
import FooterModal from "components/Button/FooterModal";
import Field from "components/Input/Field/index";
import useLancarContaRecorrente from "core/features/conta-recorrente/hooks/useLancarContaRecorrente";
import { OperacaoEnum } from "core/resources/enums/conta";
import { LancarContaRecorrenteForm } from "features/conta-recorrente/typings";
import { showSuccess } from "infra/helpers/alerta";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useContaRecorrenteDadosLancamento from "core/features/conta-recorrente/hooks/useContaRecorrenteDadosLancamento";
import moment from "moment";
import ComposicaoContaLista from "ui/components/Features/Financeiro/FormConta/components/ComposicaoContaLista";
import ModalComposicaoConta from "ui/components/Features/Financeiro/FormConta/components/ModalComposicaoConta";
import SkeletonForm from "ui/components/Features/Financeiro/FormConta/SkeletonForm";
import { AxiosError } from "axios";
import { showErrorAxios } from "infra/helpers/alerta";
interface ModalLancamentoProps {
  id: string;
  operacao: OperacaoEnum;
  isOpen: boolean;
  onClose: () => any;
  refetch?: () => any;
  mes?: string;
}

function ModalLancamento({
  id,
  operacao,
  isOpen,
  onClose,
  refetch,
  mes = moment().format("YYYY-MM"),
}: ModalLancamentoProps) {
  const [isOpenComposicaoConta, setOpenComposicaoConta] = useState(false);

  const { data, isLoading, isFetching } = useContaRecorrenteDadosLancamento(
    id,
    mes
  );

  const { mutate: lancarConta, isLoading: isLoadingSave } =
    useLancarContaRecorrente(id);

  const { control, reset, handleSubmit, watch, setValue, getValues } =
    useForm<LancarContaRecorrenteForm>({
      mode: "all",
      defaultValues: {
        composicoes: [],
        gerarCobranca: false,
      },
    });

  useEffect(() => {
    if (data) {
      reset({
        gerarCobranca: false,
        dataVencimento: data.dataVencimento,
        composicoes: data.composicoes,
      });
    }
  }, [data, reset]);

  const onError = (
    err: AxiosError<string>,
    variables: LancarContaRecorrenteForm
  ) => {
    const action =
      err?.response?.status === 409
        ? () => onSubmit({ ...variables, pularChecagemDespesa: true })
        : undefined;

    showErrorAxios(err, undefined, action);
  };

  const onSubmit = async (data: LancarContaRecorrenteForm) => {
    lancarConta(data, {
      onSuccess: () => {
        showSuccess("Conta lançada com sucesso").then(() => {
          refetch?.();
          onClose();
        });
      },
      onError,
    });
  };

  const form = watch();

  const _isLoading = isLoading || isFetching;

  return (
    <Modal
      centered
      visible={isOpen}
      footer={
        <FooterModal
          isDisabled={isFetching}
          onSubmit={() => {
            handleSubmit(onSubmit)();
          }}
          isLoadingSave={isLoadingSave!}
          onClose={onClose}
          showRegisterAnother={false}
          primaryButtonText="Lançar"
        />
      }
      title={"Lançar conta recorrente"}
      destroyOnClose
      onCancel={onClose}
      maskClosable={false}
      closable={true}
      width={"min(800px, 60vw)"}
      bodyStyle={{
        maxHeight: "60vh",
        overflow: "auto",
        overflowX: "hidden",
        paddingBottom: 16,
      }}
    >
      {_isLoading ? <SkeletonForm /> : null}
      {!_isLoading ? (
        <Flex flexDir="column" mt={-4}>
          <Flex maxW="200px" mb={4}>
            <Field
              control={control}
              name="dataVencimento"
              label="Vencimento"
              rules={{
                required: true,
                max: "2100-01-01",
              }}
              inputProps={{
                type: "date",
                max: "2100-01-01",
              }}
            />
          </Flex>

          {operacao === OperacaoEnum.Receita ? (
            <Field
              control={control}
              name="gerarCobranca"
              render={({ field }) => (
                <Checkbox
                  onChange={() => field.onChange(!field.value)}
                  isChecked={!!field.value}
                >
                  <Text fontSize={12}>Emitir boleto</Text>
                </Checkbox>
              )}
            />
          ) : null}

          <ComposicaoContaLista
            operacao={operacao}
            form={form}
            control={control}
            onAdd={() => setOpenComposicaoConta(true)}
            getValues={getValues}
          />

          {isOpenComposicaoConta ? (
            <ModalComposicaoConta
              operacao={operacao}
              isOpen={isOpenComposicaoConta}
              onClose={() => setOpenComposicaoConta(false)}
              onSubmit={(item) => {
                const items = form.composicoes;

                const newItems = items.concat(item);

                setValue("composicoes", newItems);

                setOpenComposicaoConta(false);
              }}
            />
          ) : null}
        </Flex>
      ) : null}
    </Modal>
  );
}

export default ModalLancamento;
